import { canParseAsDuration, isOrderComplete, isOrderStarted, type ExecutionReport } from '@talos/kyoko';
import { ParameterKeysEnum } from '../../../tokens/orderStrategy';

export type EndTimePrimingDecision =
  | { kind: 'none' }
  | { kind: 'duration'; duration: string }
  | { kind: 'dateTime'; endTime: string };

export type ResolveEndTimePrimingDecisionArgs = {
  order: Pick<ExecutionReport, 'Parameters' | 'EndTime' | 'DecisionStatus' | 'OrdStatus'>;
  parameters: Record<string, unknown>;
  action: 'modify' | 'resubmit';
};

/**
 * Resolves how EndTime should be primed from an incoming order payload.
 *
 * Rules:
 * - If the form has no EndTime field, do nothing.
 * - If payload carries a usable `Parameters.Duration` (and not `Parameters.EndTime`), prefer Duration mode when:
 *   - resubmit flow, or
 *   - order has not started, or
 *   - order is complete.
 * - Otherwise, for running modify flow, prime DateTime from resolved EndTime.
 */
export function resolveEndTimePrimingDecision({
  order,
  parameters,
  action,
}: ResolveEndTimePrimingDecisionArgs): EndTimePrimingDecision {
  // Case 0: Strategy/form does not expose EndTime, so there is nowhere to prime.
  const hasEndTimeField = !!parameters[ParameterKeysEnum.EndTime];
  if (!hasEndTimeField) {
    return { kind: 'none' };
  }

  // Case 1: We only run this resolver for payloads that represent Duration intent.
  // `Parameters.EndTime` is already handled by the generic parameter->field filler (same-name mapping).
  // This resolver is the Duration -> EndTime bridge, so we skip when a non-empty
  // `Parameters.EndTime` exists.
  const orderDuration =
    order.Parameters?.Duration && !order.Parameters?.EndTime && canParseAsDuration(order.Parameters.Duration)
      ? order.Parameters.Duration
      : undefined;
  if (!orderDuration) {
    return { kind: 'none' };
  }

  const hasVisibleDurationField = !!parameters[ParameterKeysEnum.Duration];
  const orderHasStarted = isOrderStarted(order.DecisionStatus);
  const orderIsComplete = isOrderComplete(order.OrdStatus);
  const parameterEndTime = order.Parameters?.EndTime;
  const resolvedEndTime = parameterEndTime ? parameterEndTime : (order.EndTime ?? '');

  const isResubmitFlow = action === 'resubmit';
  // Guard: if strategy exposes a visible Duration field, that field owns Duration input.
  // In that case we must not mirror Duration into EndTime, or we'd prime the EndTime field
  // with a value that was originally entered into a dedicated Duration field.
  const canPrimeDurationInEndTimeField =
    !hasVisibleDurationField && (isResubmitFlow || !orderHasStarted || orderIsComplete);

  // Case 2: Preserve relative duration intent whenever Duration is the active UX path.
  if (canPrimeDurationInEndTimeField) {
    return { kind: 'duration', duration: orderDuration };
  }

  const isRunningModifyFlow = action === 'modify' && orderHasStarted && !orderIsComplete;
  // Case 3: Running modify flow falls back to absolute EndTime, if available.
  if (!isRunningModifyFlow) {
    return { kind: 'none' };
  }

  if (!resolvedEndTime) {
    return { kind: 'none' };
  }

  return { kind: 'dateTime', endTime: resolvedEndTime };
}
